import { PERMISSION_TAGS, TEMPLATES_URL } from 'appConstants';
import { ResourceCollectionOptions } from 'reducers';
import {
  AccountUtils,
  FeatureFlags,
  ImmutableQuery,
  MiscUtils,
} from 'services';
import {
  Account,
  Category,
  Device,
  Subcategory,
  SubcategoryOption,
  AbridgedBpitTemplate,
  BpitTemplateFilters,
  QueryString,
  ReachTemplate,
  ReachTemplates,
} from 'types';

const NewTicketUtils = {
  getCategory: (categories: Category[], categoryName: string) => {
    return (
      categories.find(
        // for japanese we need to check for displayname
        (item) => item.name.toLowerCase() === categoryName.toLowerCase(),
      ) || ({ subcategories: [] as Subcategory[] } as Category)
    );
  },

  getDetailsFromTemplateTags: (
    categories: Category[],
    accounts: Account[],
    accountId: string,
    tags: string[],
  ) => {
    const templateTags = tags.map(MiscUtils.toNormalCase);
    const account = AccountUtils.findById(accounts, accountId);
    const category = categories.find((item) =>
      templateTags.includes(item.name.toLowerCase()),
    );
    const subcategories =
      category && account.name
        ? NewTicketUtils.getSubcategories(
            category,
            account,
            AccountUtils.isDeviceAdmin(account),
          )
        : [];
    const subCategory =
      category && subcategories.length !== 0
        ? subcategories.find((item) =>
            templateTags.includes(item.name.toLowerCase()),
          )
        : ({} as Subcategory);
    return { category, subCategory, account };
  },

  getDisabledTags: (category: Category) => {
    return category.disabledTags && category.disabledTags.length > 0
      ? category.disabledTags
      : [];
  },

  getReachTemplateData: (
    templates: ReachTemplates,
    templateKey: string,
    queryString: QueryString,
  ): ReachTemplate => {
    // Extract and rename Reach template fields; Interpolate description
    return {
      ...templates[templateKey],
      message: MiscUtils.interpolateMessage(
        templates[templateKey],
        queryString,
      ),
    } as ReachTemplate;
  },

  getSubcategories: (
    category: Category,
    account: Account,
    isDeviceAdmin: boolean,
  ): Subcategory[] => {
    const accountType = account.type.id.toLowerCase();

    return (
      category.subcategories
        /*
         * If the `account.type` isn't in the allowed `product list`,
         * it can't use that sub category.
         */
        .filter((item) => item.allowedProducts.includes(accountType))
        .filter((subcategory) => {
          const tagMap = subcategory.tags;

          if (FeatureFlags.isActive('category-tags')) {
            /*
             * If a subcategory has a tags map, and the tags map has an entry for the
             * account's type, then the list of tags for that account must contain
             * every tag from the appropriate tag map entry.
             */
            if (tagMap && tagMap[accountType]) {
              return tagMap[accountType].every(
                (tag) =>
                  tag.startsWith('product:')
                    ? account.tags.includes(tag)
                    : true,
              );
            }
          }

          /*
           * If category does not have a tags map, or the tags map does not have
           * an entry for the account type, then every allowed product is
           * permitted.
           */
          return true;
        })
        .filter((subcategory) => {
          const tagMap = subcategory.tags;
          if (FeatureFlags.isActive('category-tags')) {
            if (
              tagMap &&
              tagMap[accountType] &&
              tagMap[accountType].length > 0
            ) {
              return tagMap[accountType].some(
                (tag) =>
                  tag.startsWith('serviceBlock:')
                    ? account.tags.includes(tag)
                    : true,
              );
            }
          }
          return true;
        })
        .filter((subcategory) => {
          const disabledTags = subcategory.disabledTags;
          const accountTags = account.tags;
          if (disabledTags && disabledTags.length > 0) {
            const hasMultipleServiceBlocks = accountTags.filter(
              (accountName: string) =>
                accountName.indexOf('serviceBlock:teamsgir') > -1 ||
                accountName.indexOf('serviceBlock:teams') > -1,
            );
            if (hasMultipleServiceBlocks.length === 2) {
              if (
                !(subcategory.tags && subcategory.tags[accountType].length > 0)
              ) {
                return !disabledTags.some((tag) => accountTags.includes(tag));
              } else {
                return true;
              }
            } else {
              return !disabledTags.some((tag) => accountTags.includes(tag));
            }
          }
          return true;
        })
        .filter((subcategory) => {
          /*
           * This filter works only for dedicated users with permission of device-admin
           * We'll filter out the products with tag device-supported in the tag
           */
          if (isDeviceAdmin) {
            const tags = subcategory.tags;
            if (tags && tags[accountType]) {
              return tags[accountType].some(
                (tag) => tag === 'devices:supported',
              );
            }
          }
          return true;
        })
    );
  },

  getSubcategoryOptions: (
    subcategories: Subcategory[],
    account: Account,
  ): SubcategoryOption[] => {
    const subcategoriesMap = subcategories
      .sort(MiscUtils.sortSubCategories('name'))
      .reduce(
        (result, item) => {
          const { name, displayName, tags } = item;
          const key = name;
          if (!result[key]) {
            result[key] = {
              name: key,
              displayName: displayName,
              tags,
              isServiceBlock:
                (tags &&
                  tags[account.type.id] &&
                  tags[account.type.id].some((tag) =>
                    tag.includes('serviceBlock:'),
                  )) ||
                false,
              isAzureVmwSolution:
                (tags &&
                  tags[account.type.id] &&
                  tags[account.type.id].some((tag) =>
                    tag.includes('product:avs'),
                  )) ||
                false,
            };
          }

          return result;
        },
        {} as { [key: string]: SubcategoryOption },
      );

    return Object.keys(subcategoriesMap).map((item) => subcategoriesMap[item]);
  },

  getTagsByAccountAndProduct: (
    subcategories: Subcategory[],
    account: Account,
    product: string = '',
  ): string[] => {
    const accountTypeId = MiscUtils.get(account, 'type.id', '');
    const subcategory = subcategories.find((cat) => cat.name === product);

    return subcategory && subcategory.tags
      ? subcategory.tags[accountTypeId] || []
      : [];
  },

  getSkusByAccountAndProduct: (
    account: Account,
    subcategories: Subcategory[],
    product: string,
  ): Array<string> => {
    const accountTypeId = MiscUtils.get(account, 'type.id', '');
    return subcategories
      .filter((subcategory) => subcategory.name === product)
      .map(
        (subcategory) =>
          subcategory.skus && subcategory.skus[accountTypeId]
            ? subcategory.skus[accountTypeId]
            : [],
      )
      .reduce((accm, skus) => [...accm, ...skus]);
  },

  canSelectDevices: (
    subcategories: Subcategory[],
    account: Account,
    product: string = '',
  ) => {
    if (!FeatureFlags.isActive('category-tags')) {
      return false;
    }

    const tags = NewTicketUtils.getTagsByAccountAndProduct(
      subcategories,
      account,
      product,
    );

    return tags.includes(PERMISSION_TAGS.DEVICES_SUPPORTED);
  },

  canSelectTemplates: (
    subcategories: Subcategory[],
    account: Account,
    product: string = '',
    isRacker: boolean = false,
  ) => {
    if (!FeatureFlags.isActive('category-tags') || isRacker) {
      return false;
    }

    const tags = NewTicketUtils.getTagsByAccountAndProduct(
      subcategories,
      account,
      product,
    );

    return tags.includes(PERMISSION_TAGS.TEMPLATES_SUPPORTED);
  },

  getTemplateCollectionOptions: (
    filterOptions: BpitTemplateFilters,
    updateFunction?: (isSuccess?: boolean) => void,
  ) => {
    const options: ResourceCollectionOptions<AbridgedBpitTemplate> = {
      dataKey: '_embedded.templates',
      filter: ImmutableQuery.fromObject(filterOptions),
      resourceUrl: TEMPLATES_URL,
      retries: 0,
      onUpdate: updateFunction ? updateFunction : (isSuccess: boolean) => null,
    };
    return options;
  },

  getDisplayTextForProduct: (
    productList: SubcategoryOption[],
    selectedProduct: string,
  ): string => {
    const filteredProduct = productList.filter(
      (item) => item.name === selectedProduct,
    );

    return filteredProduct.length > 0 ? filteredProduct[0].displayName : '';
  },

  /** Returns the formatted string to use for collapsed Step Two header */
  stepTwoHeader: (
    product: string = '',
    severityLabel: string,
    severity: string = '',
    devices: Device[] = [],
    selectedTemplate?: AbridgedBpitTemplate,
  ) => {
    const result = [];
    const deviceCount = devices.length;
    const deviceSuffix = deviceCount > 1 ? 's' : '';

    result.push(
      product +
        (severity ? ` | ${severityLabel}: ${severity}` : '') +
        (deviceCount ? ` | ${deviceCount} Device${deviceSuffix}` : ''),
    );

    if (selectedTemplate) {
      result.push(selectedTemplate.name);
    }
    return result;
  },

  /**
   * Clears input value based on previous and present value.
   */
  clearSelected: (prevValue: string, value: string) => prevValue !== value,

  /**
   * Filters out category based on below logic:
   * 1) Pick the disabledTags on each Category
   * 2) Checks if any of the disabledTag is present in all the Accounts tag
   * 3) If yes, then that Category is not shown to the User
   */
  filterCategoriesBasedOnDisabledTags: (
    categories: Category[],
    accounts: Account[],
  ) => {
    return categories.filter(({ disabledTags }) => {
      // Condition is valid untill api adds in disabled tags
      if (!disabledTags) {
        return true;
      }
      return (
        disabledTags &&
        !disabledTags.some((disabledTag) => {
          return accounts.every(({ tags }) => {
            return tags.includes(disabledTag);
          });
        })
      );
    });
  },

  canSelectEmergencySeverity: (
    subcategories: Subcategory[],
    account: Account,
    product: string = '',
  ) => {
    const tags = NewTicketUtils.getTagsByAccountAndProduct(
      subcategories,
      account,
      product,
    );

    return tags.includes(PERMISSION_TAGS.PRODUCT_RAS);
  },

  containsDeviceMandatoryTag: (
    subcategories: Subcategory[],
    account: Account,
    product: string = '',
  ) => {
    const tags = NewTicketUtils.getTagsByAccountAndProduct(
      subcategories,
      account,
      product,
    );
    return tags.includes(PERMISSION_TAGS.DEVICES_MANDATORY);
  },
};

export { NewTicketUtils };
